import { Component, ElementRef, Input, ViewChild, EventEmitter, Output, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core'
import { NgIf, CommonModule } from "@angular/common";
import { LoadingComponent } from "../loading/loading.component";
import { SvgColorInheritPipe } from '../../pipes/svg-color-inherit.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../services/api.service'
import { AccountType } from '../../interfaces/api/AccountType'

@Component({
    selector: 'app-button',
    templateUrl: './button.component.html',
    standalone: true,
    imports: [
        NgIf,
        LoadingComponent,
        SvgColorInheritPipe,
        CommonModule,
        TranslateModule
    ],
    styleUrl: './button.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ButtonComponent implements OnInit{
    @ViewChild('container')
    container: ElementRef

    @Input('skin')
    skin: 'ctop' | 'we' = 'we'

    @Input('text')
    text: string

    @Input('primary')
    primary: boolean = false

    @Input('secondary')
    secondary: boolean = false

    @Input('hoverAlt')
    hoverAlt: boolean = false

    @Input('ghost')
    ghost: boolean = false

    @Input('link')
    link: boolean = false

    @Input('block')
    block: boolean = false

    @Input('disabled')
    disabled: boolean = false

    @Input('width')
    width: number | null = null

    @Input('isFullWidth')
    isFullWidth: boolean = false

    @Input('isFullHeight')
    isFullHeight: boolean = false

    @Input('class')
    class: string = ''

    @Input('loading')
    loading: boolean = false

    @Input('size')
    size: string

    @Input('textWrap')
    textWrap: boolean = true

    @Input('autoHeight')
    autoHeight: boolean = false

    @Input('showIcon')
    showIcon: boolean = false

    @Input('iconType')
    iconType: 'arrow' | 'add' | 'delete' = 'arrow'

    @Input('iconPosition')
    iconPosition: 'start' | 'end' = 'end'

    @Input('gtmLabel')
    gtmLabel?: string

    @Input('transparent')
    transparent: boolean = false

    @Output('onClick')
    onClick = new EventEmitter<boolean>()

    constructor(private api: ApiService) {
    }

    ngOnInit() {
        if(this.api.userInfo && this.api.userInfo.getValue()){
            this.skin = AccountType.isCtop(this.api.userInfo.getValue().accountType) ? 'ctop' : 'we'
        }
    }

    onClickHandler() {
        if (this.disabled || this.loading) return
        this.onClick.emit(true)
    }

    get shouldShowStartIcon(): boolean {
        return this.showIcon && this.iconPosition === 'start';
    }

    get shouldShowEndIcon(): boolean {
        return this.showIcon && this.iconPosition === 'end';
    }

    private readonly ICONS = {
        arrow: {
            left: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.0002 17.3L8.7002 12L14.0002 6.69995L14.7002 7.39995L10.1002 12L14.7002 16.6L14.0002 17.3Z" fill="currentColor"/>
            </svg>`,
            right: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.99984 17.3L15.2998 12L9.99984 6.69995L9.29984 7.39995L13.8998 12L9.29984 16.6L9.99984 17.3Z" fill="currentColor"/>
            </svg>`
        },
        add: {
            left: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M4 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>`,
            right: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M4 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>`
        },
        delete: {
            left: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>`,
            right: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>`
        }
    };

    get icon(): string {
        const direction = this.iconPosition === 'start' ? 'left' : 'right';
        return this.ICONS[this.iconType][direction];
    }

    getClasses(): string {
        const classes = [
            'cosentino-button',
            this.skin,
            'text-center',
            'select-none',
            'cursor-pointer',
            'items-center',
            'gap-2'
        ];
        if (this.isFullWidth){
            classes.push('w-full');
        }
        if(this.isFullHeight){
            classes.push('!h-full');
        }
        if (this.size === 'small') {
            classes.push('S');
        }
        if (this.size === 'medium') {
            classes.push('M');
        }
        if (this.size === 'large') {
            classes.push('L');
        }
        if (!this.size) {
            classes.push('S');
        }
        if (this.primary) {
            classes.push('primary');
        }
        if (this.secondary) {
            classes.push('secondary');
        }
        if (this.ghost) {
            classes.push('ghost');
        }
        if (!this.primary && !this.secondary && !this.link && !this.ghost) {
            classes.push('secondary');
        }
        if (this.disabled) {
            if (this.link) {
                classes.push('disabled');
            } else {
                classes.push('disabled');
            }
        }

        if (this.block) {
            classes.push('flex', 'flex-row', "w-full");
        } else {
            classes.push('inline-flex');
        }

        if (this.loading) {
            classes.push('pointer-events-none', 'cursor-wait');
        }

        if(this.transparent) {
            classes.push('!bg-transparent')
        }

        return classes.join(' ') + ' ' + this.class;
    }

    getStyle() {
        let style = ''

        if (this.width) {
            style += ' width: ' + this.width + 'px;'
        }

        if (!this.textWrap) {
            style += ' white-space: nowrap;'
        }

        if (this.autoHeight){
            style += ' height: unset;'
        }

        return style
    }
}
